import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, ArrowRight, CheckCircle2, LifeBuoy, Briefcase } from "lucide-react";
import Button from "@/components/site/Button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const channels = [
  {
    label: "Press",
    icon: Briefcase,
    testid: "contact-channel-press",
    kind: "mail",
    value: "press@cannicomply.com",
    href: "mailto:press@cannicomply.com",
    caption: "Media enquiries and press requests",
  },
  {
    label: "Web team",
    icon: LifeBuoy,
    testid: "contact-channel-webteam",
    kind: "mail",
    value: "help@cannicomply.com",
    href: "mailto:help@cannicomply.com",
    caption: "Website issues, feedback and technical help",
  },
];

const locations = [
  {
    flag: "🇬🇧",
    label: "United Kingdom",
    detail: "Remote Sales & Support",
  },
  {
    flag: "🇪🇸",
    label: "Orihuela Costa, Alicante",
    detail: "European Operations",
  },
];

export default function Contact() {
  const [state, setState] = useState({
    name: "",
    organisation: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [prefillTag, setPrefillTag] = useState("");

  useEffect(() => {
    function onPrefill(e) {
      const detail = e?.detail || {};
      setStatus("idle");
      setPrefillTag(detail.label || "");
      setState((s) => ({
        ...s,
        message: detail.message || s.message,
      }));
    }
    window.addEventListener("cannicomply:prefill-contact", onPrefill);
    return () => window.removeEventListener("cannicomply:prefill-contact", onPrefill);
  }, []);

  function update(k, v) {
    setState((s) => ({ ...s, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...state, source: "contact-section" }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Request failed");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(String(err.message || err));
    }
  }

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-14 md:py-24 border-t border-white/[0.04]"
    >
      <div className="absolute inset-0 pointer-events-none grid-lines opacity-25" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: contact information */}
          <div className="lg:col-span-5">
            <div className="eyebrow">Get in touch</div>
            <h2 className="mt-3 font-display text-4xl md:text-[44px] tracking-[-0.035em] text-white leading-[1.02] font-semibold">
              Speak to the team.
            </h2>
            <p className="mt-5 text-slate-400 leading-relaxed text-[15px] max-w-md">
              Whether you are ready for a demonstration or exploring the
              platform, we reply within four working hours. Directors and
              compliance leads receive priority scheduling.
            </p>

            <div className="mt-8 space-y-3">
              {channels.map((c) => {
                const Icon = c.icon;
                return (
                  <a
                    key={c.label}
                    href={c.href}
                    data-testid={c.testid}
                    className="group flex items-center gap-4 rounded-lg border border-white/[0.08] hover:border-[#D4A65A]/40 bg-white/[0.02] p-4 transition-colors"
                  >
                    <span className="h-10 w-10 rounded-md bg-[#D4A65A]/10 border border-[#D4A65A]/25 flex items-center justify-center shrink-0">
                      <Icon size={16} className="text-[#D4A65A]" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                        {c.label}
                      </div>
                      <div className="mt-1 text-white text-[15px] font-semibold tracking-tight break-all">
                        {c.value}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {c.caption}
                      </div>
                    </div>
                    <ArrowRight
                      size={14}
                      className="text-slate-500 group-hover:text-white transition-colors shrink-0"
                    />
                  </a>
                );
              })}
            </div>

            {/* Locations — merged inline, tight spacing to keep column balanced with form */}
            <div className="mt-6 pt-6 border-t border-white/[0.06]">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold mb-3">
                <MapPin size={11} /> Locations
              </div>
              <div className="space-y-2">
                {locations.map((l) => (
                  <div
                    key={l.label}
                    data-testid={`contact-location-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-center gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3.5 py-2.5"
                  >
                    <span className="text-xl leading-none shrink-0" aria-hidden="true">{l.flag}</span>
                    <div className="min-w-0">
                      <div className="text-white text-[13px] font-semibold tracking-tight leading-snug">
                        {l.label}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                        {l.detail}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-7 relative rounded-xl border border-white/[0.08] bg-gradient-to-br from-[#0B1424] to-[#050B18] p-6 md:p-8"
            data-testid="contact-form"
          >
            {prefillTag && status !== "success" && (
              <div
                data-testid="contact-prefill-badge"
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#D4A65A]/30 bg-[#D4A65A]/10 px-3 py-1 text-[10.5px] uppercase tracking-[0.2em] text-[#D4A65A] font-semibold"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
                {prefillTag}
              </div>
            )}
            {status !== "success" && (
              <>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label="Full name"
                    required
                    testid="contact-name"
                    value={state.name}
                    onChange={(v) => update("name", v)}
                    placeholder="Your full name"
                  />
                  <Field
                    label="Organisation"
                    testid="contact-org"
                    value={state.organisation}
                    onChange={(v) => update("organisation", v)}
                    placeholder="Your organisation"
                  />
                  <Field
                    label="Work email"
                    type="email"
                    required
                    testid="contact-emailinput"
                    value={state.email}
                    onChange={(v) => update("email", v)}
                    placeholder="you@yourorganisation.co.uk"
                  />
                  <Field
                    label="Phone (optional)"
                    testid="contact-phoneinput"
                    value={state.phone}
                    onChange={(v) => update("phone", v)}
                    placeholder="0161 000 0000"
                  />
                </div>

                <label className="mt-4 block">
                  <span
                    className="text-[12px] uppercase tracking-[0.18em] font-bold"
                    style={{ color: "#FFFFFF" }}
                  >
                    How can we help?
                  </span>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={state.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us about your organisation, team size and current compliance workflow."
                    className="mt-1.5 w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors resize-none"
                  />
                </label>

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                  <div className="text-[11px] text-slate-500">
                    Enquiries route to{" "}
                    <span className="text-slate-300">press@cannicomply.com</span>.
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "sending"}
                    variant="primary"
                    size="md"
                  >
                    {status === "sending" ? "Sending" : "Send enquiry"}
                    <ArrowRight size={14} />
                  </Button>
                </div>

                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    className="mt-4 rounded-md border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200"
                  >
                    We could not send that. {errorMsg || "Please try again."}
                  </div>
                )}
              </>
            )}

            {status === "success" && (
              <div
                data-testid="contact-success"
                className="text-center py-8 md:py-12"
              >
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4A65A]/30 bg-[#D4A65A]/10">
                  <CheckCircle2 className="text-[#D4A65A]" size={24} />
                </div>
                <h3 className="mt-5 font-display text-2xl md:text-3xl text-white tracking-[-0.02em] font-semibold">
                  Enquiry received.
                </h3>
                <p className="mt-2 text-slate-400 text-[14px] max-w-md mx-auto">
                  Your message is on its way to the sales team. Expect a reply
                  within four working hours.
                </p>
                <button
                  onClick={() => {
                    setStatus("idle");
                    setState({
                      name: "",
                      organisation: "",
                      email: "",
                      phone: "",
                      message: "",
                    });
                  }}
                  data-testid="contact-reset"
                  className="mt-6 text-[12px] text-slate-400 hover:text-white transition-colors"
                >
                  Send another enquiry
                </button>
              </div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, value, onChange, type = "text", required, placeholder }) {
  return (
    <label className="block">
      <span
        className="text-[12px] uppercase tracking-[0.18em] font-bold"
        style={{ color: "#FFFFFF" }}
      >
        {label}
        {required ? " *" : ""}
      </span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-md border border-white/40 bg-white/[0.12] px-3 py-2.5 text-[14px] text-white placeholder:text-slate-300 focus:border-[#FFD98A] focus:outline-none focus:ring-2 focus:ring-[#FFD98A]/40 transition-colors"
      />
    </label>
  );
}
